// Enhanced Milestone API Service - Lambda Version
// This replaces the direct OpenAI calls with secure Lambda endpoints

import { generateMilestonesViaLambda, isLambdaAvailable } from '../utils/lambdaConfig';

// Enhanced API Service Class for Lambda
export class EnhancedMilestoneAPIService {
  constructor() {
    this.isLambdaAvailable = isLambdaAvailable();
  }

  /**
   * Generate enhanced milestones using Lambda API
   * @param {Object} goalData - The goal information
   * @param {Object} userContext - User context and preferences
   * @returns {Promise<Object>} Enhanced milestone recommendations
   */
  async generateEnhancedMilestones(goalData, userContext = {}) {
    try {
      // Check if Lambda is available
      if (!this.isLambdaAvailable) {
        throw new Error('Lambda API not available. Please check your configuration.');
      }

      // Validate required data
      if (!goalData || !goalData.title) {
        throw new Error('Goal data with title is required');
      }

      // Get authentication token from userContext
      const authToken = userContext.authToken;
      if (!authToken) {
        throw new Error('Authentication token required for Lambda API calls');
      }

      // Build comprehensive user context
      const enhancedUserContext = this.buildEnhancedUserContext(userContext, goalData);

      // Call Lambda API
      const response = await generateMilestonesViaLambda(goalData, enhancedUserContext, authToken);
      
      // Transform the response to match expected format
      const transformedData = this.transformLambdaResponse(response, goalData);

      return {
        success: true,
        data: transformedData,
        metadata: {
          provider: 'lambda',
          generatedAt: new Date().toISOString(),
          goalTitle: goalData.title,
          category: goalData.category || 'general'
        }
      };

    } catch (error) {
      console.error('Enhanced Milestone API Error:', error);
      
      return {
        success: false,
        error: error.message,
        data: this.generateFallbackResponse(goalData),
        metadata: {
          provider: 'fallback',
          generatedAt: new Date().toISOString(),
          error: true
        }
      };
    }
  }

  /**
   * Build enhanced user context for Lambda API
   */
  buildEnhancedUserContext(userContext, goalData) {
    return {
      // Academic context
      current_level: userContext.current_level || 'Not specified',
      grade: userContext.grade || 'Not specified',
      gpa: userContext.gpa || 'Not specified',
      test_scores: userContext.test_scores || 'Not specified',
      extracurriculars: userContext.extracurriculars || 'Not specified',
      
      // Personal context
      location: userContext.location || 'Not specified',
      age: userContext.age || 'Not specified',
      interests: userContext.interests || 'Not specified',
      
      // Professional context (for career goals)
      current_role: userContext.current_role || 'Not specified',
      industry: userContext.industry || 'Not specified',
      experience_years: userContext.experience_years || 'Not specified',
      current_skills: userContext.current_skills || 'Not specified',
      
      // Financial context
      budget: userContext.budget || 'Not specified',
      current_income: userContext.current_income || 'Not specified',
      financial_goals: userContext.financial_goals || 'Not specified',
      
      // Time and availability
      time_availability: userContext.time_availability || 'Not specified',
      available_time: userContext.available_time || 'Not specified',
      
      // Learning preferences
      learning_style: userContext.learning_style || 'Not specified',
      motivation_level: userContext.motivation_level || 'Not specified',
      
      // Goal-specific context
      goal_title: goalData.title,
      goal_category: goalData.category || 'general',
      due_date: goalData.dueDate || 'Not specified',
      
      // Authentication token for Lambda
      authToken: userContext.authToken
    };
  }

  /**
   * Transform Lambda response to expected format
   */
  transformLambdaResponse(lambdaResponse, goalData) {
    try {
      // Lambda response should contain milestones data
      const milestones = lambdaResponse.milestones || lambdaResponse;
      
      // Ensure we have the expected structure
      if (milestones && typeof milestones === 'object') {
        return {
          recommendations: [{
            path1: this.formatMilestoneSteps(milestones)
          }]
        };
      }
      
      // Fallback if structure is different
      return {
        recommendations: [{
          path1: {
            step1: {
              title: "Review your goal and create a plan",
              importance: 25,
              details: "Start by clearly defining your goal and breaking it down into manageable steps."
            },
            step2: {
              title: "Set specific milestones",
              importance: 25,
              details: "Create measurable milestones with deadlines to track your progress."
            },
            step3: {
              title: "Take action and monitor progress",
              importance: 25,
              details: "Begin working on your first milestone and regularly review your progress."
            },
            step4: {
              title: "Adjust and optimize",
              importance: 25,
              details: "Based on your progress, adjust your approach and optimize your strategy."
            }
          }
        }]
      };
    } catch (error) {
      console.error('Error transforming Lambda response:', error);
      return this.generateFallbackResponse(goalData);
    }
  }

  /**
   * Format milestone steps from Lambda response
   */
  formatMilestoneSteps(milestones) {
    const steps = {};
    let stepCount = 1;
    
    // If milestones is an array, convert to steps
    if (Array.isArray(milestones)) {
      milestones.forEach((milestone, index) => {
        steps[`step${index + 1}`] = {
          title: milestone.title || `Step ${index + 1}`,
          importance: milestone.importance || Math.max(10, 100 - (index * 15)),
          details: milestone.details || milestone.description || `Complete step ${index + 1}`
        };
        stepCount = index + 1;
      });
    } else if (milestones && typeof milestones === 'object') {
      // If milestones is an object, try to extract steps
      Object.keys(milestones).forEach((key, index) => {
        if (key.startsWith('step') || key.startsWith('milestone')) {
          const milestone = milestones[key];
          steps[`step${index + 1}`] = {
            title: milestone.title || milestone.name || `Step ${index + 1}`,
            importance: milestone.importance || milestone.priority || Math.max(10, 100 - (index * 15)),
            details: milestone.details || milestone.description || milestone.instructions || `Complete step ${index + 1}`
          };
          stepCount = index + 1;
        }
      });
    }
    
    // Ensure we have at least some steps
    if (Object.keys(steps).length === 0) {
      steps.step1 = {
        title: "Define your approach",
        importance: 30,
        details: "Start by clearly defining your approach to achieving this goal."
      };
      steps.step2 = {
        title: "Create a timeline",
        importance: 25,
        details: "Establish a realistic timeline with specific deadlines."
      };
      steps.step3 = {
        title: "Take action",
        importance: 25,
        details: "Begin taking concrete steps toward your goal."
      };
      steps.step4 = {
        title: "Review and adjust",
        importance: 20,
        details: "Regularly review your progress and adjust your approach as needed."
      };
    }
    
    return steps;
  }

  /**
   * Generate fallback response when Lambda fails
   */
  generateFallbackResponse(goalData) {
    return {
      recommendations: [{
        path1: {
          step1: {
            title: "Define your goal clearly",
            importance: 30,
            details: `Clearly define what you want to achieve with "${goalData.title}". Write down specific, measurable outcomes.`
          },
          step2: {
            title: "Research and plan",
            importance: 25,
            details: "Research best practices and create a detailed plan with specific steps and deadlines."
          },
          step3: {
            title: "Take the first step",
            importance: 25,
            details: "Start with the most important or easiest step to build momentum."
          },
          step4: {
            title: "Monitor and adjust",
            importance: 20,
            details: "Regularly review your progress and adjust your approach based on what you learn."
          }
        }
      }]
    };
  }

  /**
   * Check if the service is available
   */
  isServiceAvailable() {
    return this.isLambdaAvailable;
  }
}

// Export default instance
export default new EnhancedMilestoneAPIService();